import { useSyncExternalStore } from 'react';

import {
  ROOT_LOADER_DONE_EVENT,
  ROOT_LOADER_ID,
  ROOT_LOADER_VISIBLE_EVENT,
} from 'platform/components/loader/RootLoader';

type RootLoaderListener = () => void;

const listeners = new Set<RootLoaderListener>();
let rootLoaderDoneSnapshot: boolean | null = null;
let listeningToWindow = false;

function readRootLoaderDoneFromDom(): boolean {
  return typeof document !== 'undefined' && !document.getElementById(ROOT_LOADER_ID);
}

function getRootLoaderDoneSnapshot(): boolean {
  return rootLoaderDoneSnapshot ?? readRootLoaderDoneFromDom();
}

function setRootLoaderDoneSnapshot(done: boolean): void {
  if (rootLoaderDoneSnapshot === done) {
    return;
  }
  rootLoaderDoneSnapshot = done;
  listeners.forEach((listener) => listener());
}

function markDone(): void {
  setRootLoaderDoneSnapshot(true);
}

function markVisible(): void {
  setRootLoaderDoneSnapshot(false);
}

function subscribeRootLoader(listener: RootLoaderListener): () => void {
  listeners.add(listener);
  rootLoaderDoneSnapshot = readRootLoaderDoneFromDom();
  if (!listeningToWindow && typeof window !== 'undefined') {
    window.addEventListener(ROOT_LOADER_DONE_EVENT, markDone);
    window.addEventListener(ROOT_LOADER_VISIBLE_EVENT, markVisible);
    listeningToWindow = true;
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && listeningToWindow && typeof window !== 'undefined') {
      window.removeEventListener(ROOT_LOADER_DONE_EVENT, markDone);
      window.removeEventListener(ROOT_LOADER_VISIBLE_EVENT, markVisible);
      listeningToWindow = false;
    }
  };
}

export function useRootLoaderDone(): boolean {
  return useSyncExternalStore(
    subscribeRootLoader,
    getRootLoaderDoneSnapshot,
    readRootLoaderDoneFromDom,
  );
}
